import {getTestProps} from '@benchling/js-test-utils';
import {
  Anchor,
  Button,
  Center,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  createStyles,
} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import {
  formatModelCategoryLabel,
  getAvailableModelsByCategory,
  getModelCapabilityLabels,
  MODEL_CATEGORY,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import type {ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

import GetInTouchButton from './contact-us/GetInTouchButton';
import {
  MODEL_HUB_LISTING_TABLE_VERTICAL_SPACING,
  useModelHubListingTableStyles,
} from './lib/modelHubListingTableStyles';
import ModelHubNoSearchResults from './lib/ModelHubNoSearchResults';
import ModelLabels from './lib/ModelLabels';
import ModelCard from './ModelCard';
import {MODEL_HUB_MAIN_PADDING_Y_PX} from './ModelHubConstants';
import NoModelsInstalledEmptyState from './scientific-models/NoModelsInstalledEmptyState';

type ViewMode = 'grid' | 'list';

interface ModelHubContentProps {
  models: ReadonlyArray<ScientificModelNode>;
  /** Omitted when signed out, which is what hides the feedback entry point. */
  onContactUs?: () => void;
  onNewPrediction?: (model: ScientificModelNode) => void;
  /** Hosts whose chrome already names the page (core's tabs) turn this off to avoid repeating it. */
  withTitle?: boolean;
}

export default function ModelHubContent({
  models,
  onContactUs,
  onNewPrediction,
  withTitle = true,
}: ModelHubContentProps): JSX.Element {
  const {classes, cx} = useStyles();
  const {classes: tableClasses} = useModelHubListingTableStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODELS_VIEWED);
  }, []);

  const structurePredictionModels = useMemo(
    () => getAvailableModelsByCategory(models, MODEL_CATEGORY.STRUCTURE_PREDICTION),
    [models]
  );

  const filteredModels = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (normalizedQuery.length === 0) {
      return structurePredictionModels;
    }

    return structurePredictionModels.filter((model) => {
      const searchableValues = [
        model.displayName,
        model.description,
        model.category,
        formatModelCategoryLabel(model.category),
      ];
      return searchableValues.some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [searchQuery, structurePredictionModels]);

  const sortedModels = useMemo(
    () => [...filteredModels].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [filteredModels]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const nextQuery = event.currentTarget.value;
    const hadQuery = searchQuery.trim().length > 0;
    const hasQuery = nextQuery.trim().length > 0;
    if (hasQuery) {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODELS_SEARCHED);
    } else if (hadQuery) {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODELS_SEARCH_CLEARED);
    }
    setSearchQuery(nextQuery);
  };

  const handleClearSearch = (): void => {
    if (searchQuery.trim().length > 0) {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODELS_SEARCH_CLEARED);
    }
    setSearchQuery('');
  };

  const handleReferenceClick = (event: ElementMouseEvent): void => {
    event.stopPropagation();
  };

  const handleReferenceKeyDown = (event: ElementKeyboardEvent): void => {
    event.stopPropagation();
  };

  const handleConfigureModel = (model: ScientificModelNode): void => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODEL_CARD_CONFIGURE_CLICKED, {
      model: model.externalReference,
      category: model.category,
    });
    onNewPrediction?.(model);
  };

  const renderEmptyState = (): JSX.Element => (
    <ModelHubNoSearchResults entity="models" onClearSearch={handleClearSearch} />
  );

  const renderCards = (): JSX.Element => {
    if (sortedModels.length === 0) {
      return renderEmptyState();
    }

    return (
      <div
        className={classes.cardGrid}
        {...getTestProps({component: 'ModelHubContent', element: 'cardGrid'})}
      >
        {sortedModels.map((model) => (
          <ModelCard key={model.externalReference} model={model} onNewPrediction={handleConfigureModel} />
        ))}
      </div>
    );
  };

  const renderTable = (): JSX.Element => {
    if (sortedModels.length === 0) {
      return renderEmptyState();
    }

    return (
      <Paper
        className={tableClasses.tablePaper}
        radius="md"
        withBorder={true}
        {...getTestProps({component: 'ModelHubContent', element: 'listTable'})}
      >
        <Table
          className={tableClasses.table}
          horizontalSpacing={14}
          verticalSpacing={MODEL_HUB_LISTING_TABLE_VERTICAL_SPACING}
        >
          <thead>
            <tr>
              <th>Model</th>
              <th>Description</th>
              <th>Capabilities</th>
              <th>Reference</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedModels.map((model) => (
              <tr
                key={model.externalReference}
                aria-label={`New prediction with ${model.displayName}`}
                className={classes.clickableRow}
                {...getActionButtonRoleProps({
                  onPress: () => {
                    handleConfigureModel(model);
                  },
                })}
              >
                <td>
                  <Group noWrap={true} spacing={9}>
                    <span className={classes.modelIconBox}>
                      <img alt={model.displayName} className={classes.modelIcon} src={model.iconPath} />
                    </span>
                    <Text fw={600} size="lg">
                      {model.displayName}
                    </Text>
                  </Group>
                </td>
                <td>
                  <Text className={classes.tableDescription} size="lg">
                    {model.description}
                  </Text>
                </td>
                <td>
                  <ModelLabels
                    capabilities={getModelCapabilityLabels(model)}
                    category={formatModelCategoryLabel(model.category)}
                  />
                </td>
                <td>
                  {model.paperLink ? (
                    <Anchor
                      className={classes.referenceLink}
                      href={model.paperLink}
                      onClick={handleReferenceClick}
                      onKeyDown={handleReferenceKeyDown}
                      rel="noopener noreferrer"
                      size="md"
                      target="_blank"
                    >
                      <Group noWrap={true} spacing={4}>
                        <Text inherit={true}>Reference</Text>
                        <BenchlingIcon className={classes.referenceIcon} icon="arrow-up-right" />
                      </Group>
                    </Anchor>
                  ) : (
                    <Text color="dimmed" size="md">
                      —
                    </Text>
                  )}
                </td>
                <td>
                  <Button
                    aria-hidden={true}
                    className={classes.tableActionButton}
                    color="blue"
                    component="div"
                    rightIcon={<BenchlingIcon icon="arrow-right" />}
                    size="xs"
                    tabIndex={-1}
                    variant="secondary"
                  >
                    New prediction
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Paper>
    );
  };

  const renderViewToggle = (): JSX.Element => (
    <SegmentedControl
      aria-label="View mode"
      variant="contained"
      data={[
        {
          value: 'list',
          label: (
            <Center aria-label="List view" role="img">
              <BenchlingIcon icon="list" />
            </Center>
          ),
        },
        {
          value: 'grid',
          label: (
            <Center aria-label="Card view" role="img">
              <BenchlingIcon icon="grid-dots" />
            </Center>
          ),
        },
      ]}
      onChange={(value) => {
        const nextViewMode = value as ViewMode;
        if (nextViewMode === viewMode) {
          return;
        }
        trackEvent(
          nextViewMode === 'list'
            ? MODEL_HUB_ANALYTICS_ACTIONS.MODELS_VIEW_TOGGLED_TO_LIST
            : MODEL_HUB_ANALYTICS_ACTIONS.MODELS_VIEW_TOGGLED_TO_GRID
        );
        setViewMode(nextViewMode);
      }}
      value={viewMode}
    />
  );

  if (structurePredictionModels.length === 0) {
    return (
      <Stack spacing="md" {...getTestProps({component: 'ModelHubContent', element: 'root'})}>
        {/* A tenant with no models installed is exactly who needs to reach us, so the empty state
            keeps the entry point even though it has no title row. */}
        {onContactUs != null && (
          <Group position="right">
            <GetInTouchButton onClick={onContactUs} />
          </Group>
        )}
        <NoModelsInstalledEmptyState />
      </Stack>
    );
  }

  return (
    <Stack
      className={cx(viewMode === 'list' && classes.listContent)}
      spacing="lg"
      {...getTestProps({component: 'ModelHubContent', element: 'root'})}
    >
      <Stack spacing="md">
        {(withTitle || onContactUs != null) && (
          <Group noWrap={true} pb={MODEL_HUB_MAIN_PADDING_Y_PX} position="apart">
            {withTitle && <Title order={1}>Models</Title>}
            {onContactUs != null && <GetInTouchButton onClick={onContactUs} />}
          </Group>
        )}

        <Group className={classes.toolbar} noWrap={true} position="apart" spacing="md">
          <TextInput
            aria-label="Search models"
            className={classes.searchInput}
            icon={<BenchlingIcon icon="search" />}
            onChange={handleSearchChange}
            placeholder="Search models"
            size="lg"
            value={searchQuery}
          />
          <Group className={classes.toolbarActions} noWrap={true} spacing="md">
            <Text className={classes.modelCount} color="dimmed" size="sm">
              {`${sortedModels.length} ${pluralize(sortedModels.length, 'model')}`}
            </Text>
            {renderViewToggle()}
          </Group>
        </Group>
      </Stack>

      {viewMode === 'grid' ? renderCards() : renderTable()}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  cardGrid: {
    display: 'grid',
    gap: theme.spacing.lg,
    gridTemplateColumns: 'repeat(auto-fill, minmax(336px, 1fr))',
  },
  clickableRow: {
    cursor: 'pointer',
  },
  // Grid mode grows and lets the host scroll; list mode is bounded so the table scrolls inside it.
  listContent: {
    height: '100%',
    minHeight: 0,
  },
  loaderContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: '60px 0',
  },
  modelIcon: {
    maxHeight: '80%',
    maxWidth: '80%',
    objectFit: 'contain',
  },
  modelIconBox: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[0],
    borderRadius: theme.radius.smplus,
    display: 'inline-flex',
    flex: '0 0 32px',
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  modelCount: {
    whiteSpace: 'nowrap',
  },
  referenceIcon: {
    fontSize: theme.fontSizes.xs,
  },
  referenceLink: {
    color: theme.colors.gray[6],
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  },
  searchInput: {
    maxWidth: 480,
    width: '100%',
  },
  tableActionButton: {
    pointerEvents: 'none',
  },
  tableDescription: {
    color: theme.colors.gray[7],
  },
  toolbar: {
    [theme.fn.smallerThan('sm')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  toolbarActions: {
    flexShrink: 0,

    [theme.fn.smallerThan('sm')]: {
      justifyContent: 'space-between',
      width: '100%',
    },
  },
}));
